import React, { useMemo } from 'react';
import { Theme, Typography, Button, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { useSelector } from 'react-redux';
import {
	Category,
	ProductApi,
	Languages,
	OrderProduct,
	OrderInfo,
	Shop,
	ById,
	Duration,
} from 'common/types';
import ProductCard from './ProductCard';
import ActiveProductCard from './ActiveProductCard';
import LinkButton from 'common/components/LinkButton';
import Grid from '@mui/material/Grid';
import {
	newFirestoreId,
	getActiveCategories,
	PartialOrderProductFields,
	getProductsFromIds,
} from 'common/utils/newRentalUtils';
import { getCategoryProducts } from 'common/utils/productUtils';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import * as StockSelectors from 'selectors/StockSelectors';
import { Routes } from 'routing';
import { filterByLocation } from 'common/utils/productVisibilityFilters';
import { useTranslation } from 'services/localization/useTranslation';
import { ProductTotalAvailability } from 'common/api/frontend/inventory_new';
import { includesString } from 'common/utils/searchFunctions';
import EmptyState from 'common/components/EmptyState';
import { isProductForRent } from 'common/modules/products/utils';

interface OwnProps {
	stockProducts: ProductApi[];
	rentalProducts: OrderProduct[];
	categories: Category[];
	selectedCategoryId: string;
	activeShopperId: string;
	activeShopperSegment: string | undefined;
	lang: Languages;
	duration: Duration;
	startDate: string;
	endDate: string;
	activeShop: Shop;
	rentalInfo: OrderInfo;
	productAvailabilities: ById<ProductTotalAvailability>;
	loadingProductAvailability: boolean;
	handleProductAdd: (p: OrderProduct) => void;
	handleProductDelete: (pId: string) => void;
	updateShopperAdditionalProducts: (newProducts: OrderProduct[], shopperId: string) => void;
	availabilityNotLoadedForLongDuration: boolean;
	locationId: string;
	searchInput?: string;
	handleClearSearch?: (searchInput: string) => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const ProductSelection = (props: Props) => {
	const {
		classes,
		stockProducts,
		selectedCategoryId,
		categories,
		productAvailabilities,
		activeShopperId,
		activeShopperSegment,
		activeShop,
		loadingProductAvailability,
		handleProductAdd,
		handleProductDelete,
		updateShopperAdditionalProducts,
		startDate,
		endDate,
		duration,
		availabilityNotLoadedForLongDuration,
		rentalInfo,
		locationId,
		searchInput,
		handleClearSearch,
	} = props;
	const currency = getCurrencyObjectFromCurrencyCode(
		rentalInfo.pricing.currency,
		activeShop.publicInfo.currency,
	);
	const taxExcluded = rentalInfo.pricing.taxExcluded;
	const { t, getTranslation } = useTranslation();

	const visibleStockProducts = useMemo(() => {
		return stockProducts.filter(
			(p) => !(p.hiddenFromChannels && p.hiddenFromChannels.ADMIN) && isProductForRent(p),
		);
	}, [stockProducts]);

	const search = searchInput ?? '';

	const intercomTourProduct = useSelector(StockSelectors.firstProductWithNoVariants);

	const sortProductsByName = (a: ProductApi, b: ProductApi) => {
		return a.name < b.name ? -1 : a.name === b.name ? 0 : 1;
	};

	const getActiveProducts = (stockProduct: ProductApi): OrderProduct[] => {
		const shopperRentalProducts = props.rentalProducts.filter(
			(p) => p.shopperId === activeShopperId && p.productApiId === stockProduct.id,
		);
		return shopperRentalProducts;
	};

	const getProductItemElement = (product: ProductApi) => {
		const includedActiveProducts = getActiveProducts(product);
		return (
			<Grid item xs={12} sm={4} md={3} key={product.id} data-qa="productCard">
				{includedActiveProducts.length
					? renderActiveProduct(includedActiveProducts, product)
					: renderProduct(product)}
			</Grid>
		);
	};

	const renderProductsForCategory = () => {
		const rentedStockProducts = getCategoryProducts(visibleStockProducts, selectedCategoryId);
		if (rentedStockProducts.length === 0) {
			return (
				<div className={classes.flexCenter}>
					<Typography variant="h5" gutterBottom>
						{t('newRental.noProductsInCategory', 'The category does not have any products yet')}
					</Typography>
					<LinkButton
						className={classes.newProductButton}
						variant="contained"
						to={Routes.CATALOG_CREATE_PRODUCT}
						color="secondary"
					>
						{t('newRental.createFirstProduct', 'Create first product')}
					</LinkButton>
				</div>
			);
		}
		const productElements = rentedStockProducts
			.sort(sortProductsByName)
			.filter((p) => filterByLocation(p, locationId))
			.filter((p) => includesString(getTranslation(p.name), search))
			.map((product) => getProductItemElement(product));

		return (
			<EmptyState
				isEmpty={productElements.length === 0}
				isLoading={false}
				emptyContent={
					<Box className={classes.flexCenter}>
						<Typography align="center" className={classes.header}>
							{t('common:states.noResults')}
						</Typography>
						<Button
							style={{ textAlign: 'center' }}
							variant="contained"
							color="primary"
							onClick={() => handleClearSearch?.('') ?? null}
						>
							<Typography>{t('common:actions.clear')}</Typography>
						</Button>
					</Box>
				}
				render={() => (
					<Grid container spacing={2} alignContent="flex-start" className={classes.productList}>
						{productElements}
					</Grid>
				)}
			/>
		);
	};

	const renderAllProductsWithCategories = () => {
		if (!categories) {
			return null;
		}
		if (stockProducts.length === 0) {
			return (
				<div className={classes.flexCenter}>
					<Typography variant="h5" gutterBottom>
						{t('products.noProducts', 'No products yet')}
					</Typography>
					<LinkButton
						className={classes.newProductButton}
						variant="contained"
						to={Routes.CATALOG_CREATE_PRODUCT}
						color="secondary"
					>
						{t('newRental.createFirstProduct')}
					</LinkButton>
				</div>
			);
		}
		const activeCategories = getActiveCategories(categories, stockProducts);

		const productCategoryElements = activeCategories.map((category) => {
			const productElements = getCategoryProducts(visibleStockProducts, category.id)
				.sort(sortProductsByName)
				.filter((p) => filterByLocation(p, locationId))
				.filter((p) => includesString(getTranslation(p.name), search))
				.map((product) => getProductItemElement(product));

			return (
				<Grid
					key={category.id}
					container
					spacing={2}
					alignContent="flex-start"
					className={classes.productList}
				>
					{!!productElements.length && (
						<Grid item xs={12} className={classes.headerItem}>
							<Typography variant="h6">{getTranslation(category.name)}</Typography>
						</Grid>
					)}
					{productElements}
				</Grid>
			);
		});

		const [{ props }] = productCategoryElements;

		const hasResults = !!props.children.length;

		return (
			<EmptyState
				isEmpty={!hasResults}
				isLoading={false}
				emptyContent={
					<Box className={classes.flexCenter}>
						<Typography align="center" className={classes.header}>
							{t('common:states.noResults')}
						</Typography>
						<Button
							style={{ textAlign: 'center' }}
							variant="contained"
							color="primary"
							onClick={() => handleClearSearch?.('') ?? null}
						>
							<Typography>{t('common:actions.clear')}</Typography>
						</Button>
					</Box>
				}
				render={() => <div>{productCategoryElements}</div>}
			/>
		);
	};

	const renderProduct = (stockProduct: ProductApi) => {
		const { durationInSeconds, durationName, durationType } = duration;
		const rentalId = props.rentalInfo.id;
		const orderProductId = newFirestoreId();
		const partialOrderProductFields: PartialOrderProductFields = {
			id: orderProductId,
			startDate,
			endDate,
			rentalDurationInSeconds: durationInSeconds,
			shopperId: activeShopperId,
			rentalId,
			selectedAsAdditional: false,
			durationType,
			durationName,
			startLocationId: rentalInfo.startLocation.id,
			endLocationId: rentalInfo.endLocation.id,
		};
		const savedPricingTables = activeShop.publicInfo.pricingTables;

		const isIntercomTourProduct = intercomTourProduct && intercomTourProduct.id === stockProduct.id;

		return (
			<ProductCard
				stockProduct={stockProduct}
				allStockProducts={stockProducts}
				allOrderProducts={props.rentalProducts}
				addProduct={handleProductAdd}
				partialOrderProductFields={partialOrderProductFields}
				savedPricingTables={savedPricingTables}
				startDate={startDate}
				endDate={endDate}
				shopId={props.activeShop.shopInfo.id}
				productAvailability={productAvailabilities[stockProduct.id]}
				loadingProductAvailability={loadingProductAvailability}
				availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
				activeShopperSegment={activeShopperSegment}
				currencyCode={currency.code}
				taxExcluded={taxExcluded}
				data-intercom-target={isIntercomTourProduct ? 'First product with no variants' : null}
			/>
		);
	};

	const renderActiveProduct = (includedOrderProducts: OrderProduct[], stockProduct: ProductApi) => {
		const { durationInSeconds, durationName, durationType } = duration;
		const { rentalProducts } = props;
		const rentalId = props.rentalInfo.id;
		const orderProductId = newFirestoreId();
		const shopperAdditionalProducts = rentalProducts.filter(
			(p) => p.selectedAsAdditional && p.shopperId === activeShopperId,
		);
		const additionalProducts = getProductsFromIds(
			stockProduct.additionalProductIds || [],
			stockProducts,
		);
		const activeAdditionalProducts = shopperAdditionalProducts.filter((shopperAdditionalProduct) =>
			additionalProducts.some((p) => p.id === shopperAdditionalProduct.productApiId),
		);
		const shopperProductsWithoutActiveAdditionalProducts = rentalProducts.filter(
			(p) =>
				p.shopperId === activeShopperId &&
				!activeAdditionalProducts.some((activeProduct) => activeProduct.id === p.id),
		);
		const partialOrderProductFields: PartialOrderProductFields = {
			id: orderProductId,
			startDate,
			endDate,
			rentalDurationInSeconds: durationInSeconds,
			shopperId: activeShopperId,
			rentalId,
			selectedAsAdditional: true,
			durationType,
			durationName,
			startLocationId: rentalInfo.startLocation.id,
			endLocationId: rentalInfo.endLocation.id,
		};
		const savedPricingTables = props.activeShop.publicInfo.pricingTables;
		return (
			<ActiveProductCard
				orderProducts={includedOrderProducts}
				allOrderProducts={props.rentalProducts}
				stockProduct={stockProduct}
				allStockProducts={stockProducts}
				addProduct={handleProductAdd}
				removeProduct={handleProductDelete}
				additionalProducts={additionalProducts}
				activeAdditionalProducts={activeAdditionalProducts}
				updateAdditionalProducts={(selectedAdditionalProducts) =>
					updateShopperAdditionalProducts(
						[...shopperProductsWithoutActiveAdditionalProducts, ...selectedAdditionalProducts],
						activeShopperId,
					)
				}
				partialOrderProductFields={partialOrderProductFields}
				savedPricingTables={savedPricingTables}
				startDate={startDate}
				endDate={endDate}
				shopId={props.activeShop.shopInfo.id}
				productAvailability={props.productAvailabilities[stockProduct.id]}
				loadingProductAvailability={loadingProductAvailability}
				availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
				currencyCode={currency.code}
				taxExcluded={taxExcluded}
			/>
		);
	};

	return selectedCategoryId !== 'all'
		? renderProductsForCategory()
		: renderAllProductsWithCategories();
};

const styles = (theme: Theme) =>
	createStyles({
		flexCenter: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
		},
		newProductButton: {
			marginTop: 16,
		},
		productList: {
			paddingTop: 16,
		},
		headerItem: {
			paddingBottom: '0px !important',
		},
		header: {
			padding: theme.spacing(2),
			color: theme.palette.colors.tundra.main,
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(1),
			},
		},
	});

export default withStyles(styles)(ProductSelection);
